import { useCallback, useEffect, useRef, useState } from "react";
import { useAcoes, useCasoAtual, useEstado, useJanela, useOcupado, usePode } from "../estado.jsx";
import { useNavegacao } from "../navegacao.jsx";
import { Botao, Cortina, Falha, Fechar, PecaNascendo } from "../componentes/comuns.jsx";
import { Card } from "@/ui/card";
import { Item, ItemActions, ItemContent, ItemDescription, ItemMedia, ItemTitle } from "@/ui/item";
import { Separator } from "@/ui/separator";
import { confirmar } from "@/componentes/pergunta.tsx";
import { insumosDaPeca } from "../caso.js";
import { dataHora } from "../visual.jsx";
import ChatDaPeca from "./ChatDaPeca.jsx";

/* O motor de .docx é o pedaço mais pesado da tela (fonte, layout, OOXML). Só
   carrega quando alguém abre a petição — a mesa não paga por ele.

   Sem `lazy`/`Suspense` de propósito: a promessa do `import()` que não volta
   (rede caída, aba congelada) deixava o `Suspense` em "Abrindo o editor…" para
   sempre, sem saída. Aqui ela tem TETO, e passar dele vira falha com
   "Tentar de novo". Carregado uma vez, o módulo fica na variável. */
const TETO_DO_EDITOR = 15_000;
let editorCarregado = null;

function useEditorDaPeca() {
  const [estado, setEstado] = useState(() => ({ Editor: editorCarregado }));
  const carregar = useCallback(() => {
    let relogio;
    Promise.race([
      import("./EditorPeca.jsx"),
      new Promise((_, falhar) => {
        relogio = setTimeout(() => falhar(new Error("demorou demais")), TETO_DO_EDITOR);
      }),
    ])
      .then((m) => {
        editorCarregado = m.default;
        setEstado({ Editor: m.default });
      })
      .catch((erro) => setEstado({ erro }))
      .finally(() => clearTimeout(relogio));
  }, []);
  useEffect(() => {
    if (!editorCarregado) carregar();
  }, [carregar]);
  const tentarDeNovo = useCallback(() => {
    setEstado({});
    carregar();
  }, [carregar]);
  return { ...estado, tentarDeNovo };
}

/**
 * Fatos à esquerda, a peça de verdade à direita.
 *
 * À esquerda, o que a máquina extraiu da conversa: cada fato entra ou não entra
 * na petição, e é o mesmo dado da tela de Provas (`src/provas.ts`) — marcar aqui
 * muda lá. À direita, o .docx que vai ao fórum, aberto no editor: nada de prévia
 * em markdown, porque o que a advogada revisa é o arquivo.
 */

/**
 * Uma linha do pré-voo da peça: um insumo que a minuta lê, no estado em que
 * está, com o atalho para resolver quando falta.
 *
 * `estado` é o que a cor e o sinal dizem: "ok" o que já está na mão, "aviso" o
 * que a peça aceita mas piora (sem tese a busca no acervo vira chute, sem
 * documento aprovado a peça sai sem lista de provas) e "falta" o que vira
 * "[A CONFERIR]" no arquivo que a advogada vai revisar.
 */
/*
 * Os cinco estados da peça, na ordem da esteira (`ESTADOS_PECA`, em
 * `src/fluxo.ts`). Lista fechada de código, como os gatilhos dos fluxos: estado
 * novo entra aqui, ou a faixa mostra a chave crua — que é feio e visível, e não
 * silencioso.
 */
const PASSOS_DA_PECA = [
  ["a_elaborar", "A elaborar"],
  ["em_elaboracao", "Em elaboração"],
  ["revisao", "Revisão"],
  ["a_protocolar", "A protocolar"],
  ["protocolada", "Protocolada"],
];

/**
 * A faixa de situação da peça — onde ela está e o que falta para protocolar.
 *
 * É a resposta à queixa de 27/08: "o compositor de petição não guia o usuário".
 * A tela mostrava o editor, uma barra com dois botões e uma coluna de conversa —
 * e em lugar nenhum dizia em que ponto do caminho a peça estava, nem o que
 * faltava nela. Quem abria via uma folha de texto e adivinhava.
 *
 * Ela LÊ e não move: mover a peça entre estados é da esteira, e um controle aqui
 * seria uma segunda verdade sobre a mesma linha. O que ela oferece são as duas
 * contas que decidem se a peça pode ir adiante — fatos dentro dela e campos da
 * ficha que ainda vão virar "[A CONFERIR]" —, e cada uma leva a onde se resolve.
 */
function SituacaoDaPeca({ peca, completude, faltam, aoAbrirFicha, aoApagar, aoAbrirConversa, aoFechar }) {
  const i = PASSOS_DA_PECA.findIndex(([chave]) => chave === peca.estado);
  const dentro = completude?.dentro ?? 0;
  const total = completude?.total ?? 0;
  const fatosProntos = total > 0 && dentro >= total;

  return (
    <div className="flex flex-none flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-b-border bg-muted px-4 py-2">
      {/* Os cinco passos como barra segmentada, o mesmo idioma do trilho do
          processo: a cor diz o que passou e onde está, sem escrever cinco nomes
          numa faixa de 34px. Só o atual leva o nome. */}
      <div
        className="flex flex-none items-center gap-[3px]"
        role="img"
        aria-label={`Peça em "${PASSOS_DA_PECA[i]?.[1] ?? peca.estado}", passo ${i + 1} de ${PASSOS_DA_PECA.length}`}
      >
        {PASSOS_DA_PECA.map(([chave, rotulo], n) => (
          <span
            key={chave}
            className={`flex h-[20px] items-center justify-center rounded-[4px] ${
              n === i ? "bg-verde-fundo px-2.5" : n < i ? "w-[14px] bg-verde" : "w-[14px] bg-foreground/12"
            } ${n === 0 ? "rounded-l-full" : ""} ${n === PASSOS_DA_PECA.length - 1 ? "rounded-r-full" : ""}`}
          >
            {n === i ? <span className="text-selo font-bold whitespace-nowrap text-creme">{rotulo}</span> : null}
          </span>
        ))}
      </div>

      {/* As duas contas que travam o protocolo, cada uma levando a onde se
          resolve. Verde quando não trava nada — o estado bom também precisa ser
          visível, senão a pessoa procura o problema que não existe. */}
      <span className={`flex-none font-mono text-selo font-bold ${fatosProntos ? "text-verde" : "text-ambar-escuro"}`}>
        {fatosProntos ? "✓" : "○"} fatos {dentro}/{total}
      </span>
      {faltam?.length ? (
        <button
          type="button"
          onClick={aoAbrirFicha}
          data-dica="Preencher os campos que a peça vai furar com [A CONFERIR]"
          className="alvo flex-none cursor-pointer border-none bg-transparent p-0 font-mono text-selo font-bold text-primary underline decoration-dotted underline-offset-2 hover:decoration-solid"
        >
          ▲ ficha: {faltam.length === 1 ? "1 campo" : `${faltam.length} campos`} em branco
        </button>
      ) : (
        <span className="flex-none font-mono text-selo font-bold text-verde">✓ ficha completa</span>
      )}
      <span className="min-w-0 flex-1" />
      {/*
       * QUEM escreveu isto — e a resposta importa quando não foi ninguém.
       *
       * A peça pode aparecer montada sem pedido (`minuta.minutarSePuder`, quando
       * a ficha está cheia e há fato marcado). Documento que nasce sozinho e não
       * diz que nasceu sozinho é a tela mentindo: quem abre precisa saber se
       * aquele texto é trabalho de um colega — que não se reescreve sem falar com
       * ele — ou proposta de máquina, que se corta à vontade.
       */}
      {peca.autor_da_versao?.includes("automático") ? (
        <span
          data-dica="A IA montou este rascunho do acervo, sem ninguém pedir — porque a ficha estava completa e havia fato marcado. Corte à vontade."
          className="flex-none rounded-md bg-verde-fundo/12 px-[7px] py-[2px] text-selo font-bold text-verde-fundo"
        >
          montada sozinha
        </span>
      ) : null}
      {/* A versão e a data só onde há largura: num telefone elas empurravam a
          faixa para duas linhas para dizer o que ninguém procura ali. */}
      <span className="hidden flex-none font-mono text-selo text-muted-foreground md:inline">
        v{peca.versao}
        {/* A hora no fuso de quem lê: o corte do texto do banco mostrava
            "09-24 01:44" (UTC) numa peça salva às 22:44 do dia 23. */}
        {peca.atualizado_em ? ` · ${dataHora(peca.atualizado_em)}` : ""}
      </span>
      {/*
       * Apagar mora AQUI, e não no pé da coluna da conversa.
       *
       * Era um botão de largura inteira com moldura terracota embaixo do
       * contador de fatos: a ação mais destrutiva da tela era a mais visível
       * dela, e parecia o próximo passo. Aqui é texto na faixa que fala DA PEÇA,
       * que é o assunto dele — e ele era a única razão de existir aquele rodapé,
       * que saiu junto: um divisor horizontal a menos, e o contador de fatos
       * deixou de ser dito duas vezes na mesma tela.
       *
       * A pergunta antes continua na ação (`apagarPeca`, `confirmar({ perigo })`).
       */}
      {/* No celular a conversa é FOLHA, e este é o caminho para ela: as duas
          superfícies não cabem empilhadas — o editor ficava com um terço da
          altura de um telefone e a conversa com o resto, e nenhuma das duas dava
          para usar. */}
      {aoAbrirConversa ? (
        <Botao
          rotulo="Conversa"
          aoTocar={aoAbrirConversa}
          titulo="A conversa sobre esta peça — ajustar o texto, buscar no acervo, perguntar ao cliente"
        />
      ) : null}
      {aoApagar ? (
        <button
          type="button"
          onClick={aoApagar}
          data-dica="Apaga o rascunho desta peça — pergunta antes"
          className="alvo flex-none cursor-pointer border-none bg-transparent p-0 text-selo font-semibold text-primary underline decoration-dotted underline-offset-2 hover:decoration-solid"
        >
          apagar
        </button>
      ) : null}
      {/* Abaixo de 980px a conversa vira folha e leva junto o único ✕ da tela:
          a petição ficava sem saída visível, fechável só pelo botão do
          navegador — que leva a mesa junto. */}
      {/* `order-first`: na ponta ele caía sozinho numa terceira linha da faixa. */}
      {aoFechar ? (
        <span className="order-first flex flex-none">
          <Fechar aoTocar={aoFechar} titulo="Fechar a petição (Esc)" lado={30} />
        </span>
      ) : null}
    </div>
  );
}

function Insumo({ estado, rotulo, detalhe, primeira = false, children }) {
  const [cor, sinal] =
    estado === "ok"
      ? ["var(--color-verde-fundo)", "●"]
      : estado === "falta"
        ? ["var(--color-terra)", "▲"]
        : ["var(--color-ambar-escuro)", "○"];
  return (
    <>
      {/* A linha que separava era `border-top` em toda linha; o `Separator` é a
          mesma régua com o papel de separador declarado. A primeira não leva:
          quem faz a divisa dela é a borda do cabeçalho do cartão. */}
      {primeira ? null : <Separator />}
      <Item size="sm" className="flex-nowrap items-start gap-2.5 rounded-none border-0 p-[11px_13px]">
        <ItemMedia className="text-apoio leading-[1.7]" style={{ color: cor }} aria-hidden="true">
          {sinal}
        </ItemMedia>
        <ItemContent className="gap-[3px]">
          <ItemTitle className="line-clamp-none block text-corpo font-semibold">{rotulo}</ItemTitle>
          {/* Em "ok" o detalhe é texto de apoio; nos outros dois ele é o próprio
              aviso, e por isso herda a cor do sinal. */}
          <ItemDescription
            className="line-clamp-none text-apoio leading-[1.5] text-tinta-suave"
            style={estado === "ok" ? undefined : { color: cor }}
          >
            {detalhe}
          </ItemDescription>
        </ItemContent>
        {children ? <ItemActions>{children}</ItemActions> : null}
      </Item>
    </>
  );
}

/**
 * A tela de antes da peça existir: o que a minuta vai ler, item por item.
 *
 * Antes era um parágrafo prometendo "usando os fatos marcados à direita" e um
 * botão — e a pessoa só descobria o que faltou depois de esperar um minuto pelo
 * modelo, num recado que sumia em seis segundos. Os insumos são exatamente os
 * que `src/minuta.ts` monta no prompt (ficha, fatos marcados, conversa como
 * apoio, documentos aprovados, reunião, acervo): a lista aqui é o espelho de lá,
 * e mexer em um pede mexer no outro.
 */
/**
 * A tela de antes da peça existir.
 *
 * Antes era um parágrafo prometendo "usando os fatos marcados à direita" e um
 * botão — e a pessoa só descobria o que faltou depois de esperar um minuto pelo
 * modelo, num recado que sumia em seis segundos. Agora o que falta aparece
 * ANTES, com o atalho de resolver na própria linha.
 */
/**
 * Antes de montar: o que entra na peça, e o que fazer com o que falta.
 *
 * Ela já listava os cinco insumos com o estado de cada um — e era aí que ela
 * parava. Três das cinco linhas apontavam um problema e não ofereciam saída
 * nenhuma ("2 campos em branco viram [A CONFERIR]", "nenhum documento
 * conferido", "sem reunião transcrita"), a ficha imprimia a CHAVE do banco
 * (`cpf, resumo_atendimento`) para quem atende, e o botão de montar ficava
 * aceso do mesmo jeito com cinco de cinco ou com duas — sem dizer que a peça ia
 * nascer com furos.
 *
 * Agora: toda linha que aponta problema leva a onde ele se resolve, o rótulo é o
 * do esquema, e acima do botão está escrito quantos furos a peça vai ter se
 * alguém montar agora. Montar continua liberado — a pressa é decisão de quem
 * atende, e às vezes a peça com dois "[A CONFERIR]" é melhor que peça nenhuma.
 * O que não pode é a tela deixar isso implícito.
 */
function Preparo({
  nome,
  caso,
  provas,
  ficha,
  esquema,
  passoDaFicha,
  aoPreencherFicha,
  aoExtrairFatos,
  aoMontar,
  aoAbrirFicha,
  aoConferir,
  aoGravarReuniao,
  aoFechar,
  ocupado,
}) {
  const insumos = insumosDaPeca({ caso, provas, ficha, esquema });
  const prontos = insumos.filter((i) => i.estado === "ok").length;
  const furos = ficha?.faltam?.length ?? 0;

  /*
   * A saída de cada linha — e por que cada uma é onde é.
   *
   * `fatos` é o único que se resolve AQUI (a extração é da própria tela); os
   * outros três moram em outro lugar da mesa, e mandar para lá é fechar a
   * petição. Fechar não perde nada: a peça ainda não existe, e é justamente por
   * isso que esta tela está aberta.
   */
  const acoes = {
    tese:
      !provas.length && !String(caso.registro?.dados?.tese ?? "").trim()
        ? { rotulo: "Preencher a tese", aoTocar: aoAbrirFicha }
        : null,
    fatos: !provas.length
      ? {
          rotulo: ocupado === "fatos" ? "Lendo…" : "Extrair da conversa",
          aoTocar: aoExtrairFatos,
          desabilitado: ocupado === "fatos",
          titulo: "Lê a conversa e propõe fatos, provas e pedidos",
        }
      : /* Extraído e não marcado: marcar é gesto da coluna ao lado, e a linha diz
           isso em vez de oferecer um botão que não existe. */
        null,
    /* Preencher primeiro, montar depois: o passo é do servidor e traz o valor
       com a fonte ("CPF: 123… (mensagem de 2026-08-14)"). Sem proposta do
       servidor, a ficha se preenche à mão — e o caminho é a ficha. */
    ficha: passoDaFicha
      ? {
          rotulo: "Preencher do que o sistema já sabe",
          aoTocar: () => aoPreencherFicha(passoDaFicha),
          titulo: passoDaFicha.detalhe,
        }
      : furos
        ? { rotulo: "Abrir a ficha", aoTocar: aoAbrirFicha, titulo: "Preencher os campos à mão, na ficha do caso" }
        : null,
    documentos: { rotulo: "Conferir documentos", aoTocar: aoConferir, titulo: "Abre um por um, com o arquivo grande" },
    reuniao: { rotulo: "Gravar a reunião", aoTocar: aoGravarReuniao, titulo: "O gravador fica na coluna do atendimento" },
  };

  return (
    <div className="relative flex min-w-0 flex-1 flex-col items-center justify-center gap-3.5 overflow-y-auto p-7">
      {/* O ✕ mora aqui porque a coluna da conversa — que era quem o tinha — não é
          montada sem peça. Superfície sobreposta sem saída visível é a que se
          fecha pelo botão do navegador, e ele leva a mesa junto. */}
      <span className="absolute top-4 right-4">
        <Fechar aoTocar={aoFechar} titulo="Fechar a petição (Esc)" lado={30} />
      </span>
      <div className="max-w-[440px] text-center">
        <div className="font-serif text-[24px] leading-[1.2]">Montar a inicial de {nome}</div>
        <div className="mt-2 text-corpo leading-[1.6] text-muted-foreground">
          A peça nasce da forma das iniciais já protocoladas pela casa e do que está listado abaixo.
        </div>
      </div>

      <Card className="w-full max-w-[480px] gap-0 rounded-[16px] border border-foreground/10 shadow-none ring-0 [--card-spacing:0px]">
        <div className="flex items-baseline gap-2 border-b border-b-border bg-foreground/3 p-[10px_14px]">
          <span className="text-selo font-bold tracking-[.08em] text-muted-foreground uppercase">O que entra na peça</span>
          {/* A conta, na cabeça do cartão: é o que diz de relance se vale montar
              agora ou resolver uma linha antes. */}
          <span className="ml-auto font-mono text-selo font-bold text-verde-fundo">
            {prontos}/{insumos.length}
          </span>
        </div>
        {insumos.map((i, n) => (
          <Insumo key={i.chave} estado={i.estado} rotulo={i.rotulo} detalhe={i.detalhe} primeira={!n}>
            {/* Só o que NÃO está ok ganha botão: ação ao lado de linha resolvida é
                convite para desfazer o que já está feito. */}
            {i.estado !== "ok" && acoes[i.chave] ? (
              <Botao
                rotulo={acoes[i.chave].rotulo}
                aoTocar={acoes[i.chave].aoTocar}
                desabilitado={acoes[i.chave].desabilitado}
                titulo={acoes[i.chave].titulo}
              />
            ) : null}
          </Insumo>
        ))}
      </Card>

      <div className="flex w-full max-w-[480px] flex-col items-center gap-2">
        <Botao
          rotulo={ocupado === "minuta" ? "Montando…" : "Montar do acervo"}
          aoTocar={aoMontar}
          desabilitado={ocupado === "minuta"}
          destaque
          estilo="font-size:14px;font-weight:600;padding:12px 22px;border-radius:12px"
        />
        {/*
         * O que a peça vai ter de furo se alguém montar AGORA.
         *
         * O botão ficava igual com cinco de cinco e com duas de cinco, e o preço
         * da pressa só aparecia depois, escrito "[A CONFERIR]" no meio do
         * documento. Dizer antes não é travar: montar continua liberado.
         */}
        <div className="max-w-[380px] text-center text-apoio leading-[1.5] text-muted-foreground">
          {furos ? (
            <>
              <span className="font-semibold text-ambar-escuro">
                A peça vai sair com {furos} furo{furos === 1 ? "" : "s"} marcado{furos === 1 ? "" : "s"} “[A CONFERIR]”.
              </span>{" "}
              Dá para montar assim e preencher depois.
            </>
          ) : (
            "Leva perto de um minuto e o texto aparece nascendo — dá para cancelar no meio."
          )}
        </div>
      </div>
    </div>
  );
}

export default function TelaPeticao() {
  const { caso, provas, rascunhoPeca, campos: esquema } = useEstado();
  const ocupado = useOcupado();
  const empilha = useJanela((j) => j.largura < 980);
  const { alternarFato, extrairFatos, gerarMinuta, cancelarMinuta, avisar, executarPasso, apagarPeca, apontarMensagem } =
    useAcoes();
  const pode = usePode();
  /* O motor do editor, entregue pelo EditorPeca quando o .docx abre: é o que
     deixa a coluna de fatos escrever na peça sem passar pelo servidor. */
  const motor = useRef(null);
  /* O trecho marcado na folha, contado pelo editor: é o "isto" de "reescreva
     isto" na conversa ao lado. */
  const [trechoMarcado, setTrechoMarcado] = useState("");
  const sincronizarPeca = useRef(null);

  /*
   * Escrever no .docx a alteração que a IA propôs.
   *
   * Mora aqui e não na conversa porque quem tem o motor é esta tela — a coluna
   * do chat conversa, não edita. É localizar-e-substituir feito à mão: marca a
   * ocorrência e digita por cima, para cada troca entrar como edição comum do
   * documento, com formatação preservada e um passo de desfazer próprio
   * (Ctrl+Z devolve).
   */
  const aplicarEdicaoNaPeca = useCallback(
    (edicao) => {
      const ed = motor.current;
      if (!ed) {
        avisar("A peça ainda não está aberta.");
        return false;
      }
      let passos = 0;
      for (let volta = 0; volta < 40; volta++) {
        const [achado] = ed.findMatches(edicao.procurar) ?? [];
        if (!achado) break;
        const selecao = ed.selectMatch(achado);
        if (selecao && !selecao.ok) break;
        const r = ed.exec(edicao.substituir ? { type: "insertText", text: edicao.substituir } : { type: "deleteText" });
        if (r && !r.ok) break;
        passos++;
        /* Texto novo que contém o antigo entraria em laço: uma volta basta. */
        if (String(edicao.substituir ?? "").includes(edicao.procurar)) break;
      }
      if (!passos) {
        avisar("Não achei esse trecho na peça — pode já ter sido alterado. Peça de novo à IA.");
        return false;
      }
      avisar(`${passos === 1 ? "1 alteração aplicada" : `${passos} alterações aplicadas`} — Ctrl+Z desfaz.`);
      return true;
    },
    [avisar],
  );
  /* O editor diz quando tem edição não salva; a tela precisa disso para não
     jogar a peça fora num Esc reflexo. */
  const naoSalvo = useRef(false);
  const moldura = useRef(null);
  /* Estável: função nova a cada render faria o efeito do editor rodar de novo
     em toda tecla digitada na peça. */
  const marcarNaoSalvo = useCallback((v) => (naoSalvo.current = v), []);
  const { ajustar, fatoApontado } = useNavegacao();
  const atual = useCasoAtual();

  /*
   * Esta tela cobre a mesa, então se comporta como caixa: Esc fecha e o foco
   * volta para onde estava. O que está atrás fica `inert` pela Casca — é lá que
   * a mesa some do alcance do teclado e do leitor de tela.
   *
   * `fecharPeticao` é o MESMO caminho do ✕, e pergunta quando há edição não
   * salva: sem isso, Esc no meio da revisão apagaria a peça em silêncio.
   */
  const fecharPeticao = useCallback(async () => {
    if (
      naoSalvo.current &&
      !(await confirmar({
        titulo: "Fechar a petição sem salvar?",
        texto: "O que você editou na peça e não salvou se perde.",
        acao: "Fechar sem salvar",
        perigo: true,
      }))
    )
      return;
    ajustar({ peticao: null, fato: null });
  }, [ajustar]);

  useEffect(() => {
    const veioDe = document.activeElement;
    /* O foco entra na tela: o botão que a abriu acabou de ficar inerte, e foco
       em elemento inerte cai no corpo da página. */
    moldura.current?.focus();
    const tecla = (e) => {
      /* `defaultPrevented` respeita quem tratou o Esc primeiro: a caixa de
         pergunta e os menus do motor de .docx cancelam o evento, e fechar a
         petição por baixo deles levaria a peça embora junto. */
      if (e.key !== "Escape" || e.defaultPrevented) return;
      fecharPeticao();
    };
    window.addEventListener("keydown", tecla);
    return () => {
      window.removeEventListener("keydown", tecla);
      veioDe?.focus?.();
    };
  }, [fecharPeticao]);

  /* Gancho ANTES da saída antecipada: depois de um `return` a ordem dos ganchos
     muda entre renders e o React perde o pareamento. */
  const [conversaAberta, setConversaAberta] = useState(false);

  if (!atual || !caso) return null;

  const inserirNaPeca = (fato) => {
    const editor = motor.current;
    if (!editor) return avisar("Abra a peça primeiro — o texto entra no ponto do cursor.");
    editor.focus();
    /* Parágrafo novo antes do texto, para o fato não emendar na frase em que o
       cursor parou. A quebra vai dentro do texto: `splitParagraph` é comando de
       documento e o editor em árvore o recusa. */
    const r = editor.exec({ type: "insertText", text: `\n${fato.detalhe || fato.rotulo}` });
    if (r && !r.ok) return avisar(r.reason ?? "Não deu para escrever na peça aqui.");
    return avisar("Trecho escrito na peça — Ctrl+Z desfaz, Salvar grava.");
  };

  /* A ficha se resolve no painel do atendimento, na seção FICHA DO CLIENTE —
     fechar a petição é o caminho, e não um segundo formulário de ficha aqui. */
  const abrirFicha = () => {
    fecharPeticao();
    ajustar({ painel: "ficha" });
  };

  /*
   * Onde empilha, a conversa é FOLHA e não coluna.
   *
   * Abaixo de 980px as duas superfícies ficavam uma sobre a outra: num telefone
   * de 880px de altura o editor — que é o trabalho — ficava com um terço, e a
   * conversa com o resto. Nenhuma das duas dava para usar. A folha põe uma coisa
   * por vez na tela e usa o gesto que o aparelho já tem.
   */
  const peca = (caso.pecas ?? [])[0];
  const completude = caso.completude;
  /* A resposta já vem do servidor ANTES do clique: `faltam` são os campos em
     branco que a peça vai furar com "[A CONFERIR]" e `campos` é o que o sistema
     já sabe, com a fonte de cada valor. Antes, quantos furos a peça teria só
     aparecia depois, num recado que desaparecia em seis segundos. */
  const ficha = caso.pauta?.ficha;
  const passoDaFicha = (caso.pauta?.passos ?? []).find((p) => p.acao === "aplicar_ficha");

  /* Desenhada UMA vez e posta em dois lugares: coluna onde há largura, folha
     onde não há. Duplicar o JSX faria a folha envelhecer sozinha — e é a folha
     que a maioria vai usar, porque a maioria abre no telefone. */
  /*
   * O ✕ da conversa fecha COISAS DIFERENTES nos dois lugares, e por isso ele é
   * parâmetro.
   *
   * Na coluna ele é a saída da petição (é o único ✕ da tela). Na folha ele fecha
   * a FOLHA — dentro dela, "fechar a petição" é sair de duas superfícies com um
   * toque, e quem abriu a conversa para ler uma sugestão perderia o documento de
   * vista sem pedir.
   */
  const conversaCom = (aoFechar) =>
    peca ? (
      <ChatDaPeca
        peca={peca ?? { id: null }}
        trecho={trechoMarcado}
        provas={provas ?? []}
        aoFechar={aoFechar}
        aoAlternarFato={alternarFato}
        aoVerNaConversa={(mid) => {
          fecharPeticao();
          apontarMensagem(mid);
        }}
        aoAplicarEdicao={aplicarEdicaoNaPeca}
        /* Grava o .docx antes de a IA ler: sem isso ela responde sobre uma
           versão que já não é a da tela — e o trecho marcado "não existe". */
        aoSincronizar={() => sincronizarPeca.current?.()}
        aoInserirFato={peca ? inserirNaPeca : null}
        fatoApontado={fatoApontado}
      />
    ) : null;

  return (
    <div
      ref={moldura}
      role="dialog"
      aria-modal="true"
      aria-label={`Petição de ${atual.nome}`}
      tabIndex={-1}
      /* A moldura é COLUNA: a faixa de situação atravessa a tela inteira em cima,
         e as duas colunas de trabalho ficam embaixo dela. Antes a raiz era a
         própria fileira, e não havia onde pôr algo que valesse para as duas. */
      className="absolute inset-0 z-[38] flex animate-[vUp_var(--t-media)_var(--e-entra)_both] flex-col overflow-hidden bg-background outline-none"
    >
      {peca ? (
        <SituacaoDaPeca
          peca={peca}
          completude={completude}
          faltam={ficha?.faltam}
          aoAbrirFicha={abrirFicha}
          aoApagar={peca.estado !== "protocolada" && pode("esteira:apagar") ? () => apagarPeca(peca) : null}
          aoAbrirConversa={empilha ? () => setConversaAberta(true) : null}
          aoFechar={empilha ? fecharPeticao : null}
        />
      ) : null}

      <div className={`flex min-h-0 flex-1 ${empilha ? "flex-col" : "flex-row"}`}>
        {peca ? (
          <Peca
            peca={peca}
            nome={atual.nome}
            aoPronto={(ed) => (motor.current = ed)}
            aoMudarSujo={marcarNaoSalvo}
            /* O trecho marcado na folha é o assunto do pedido na coluna ao lado —
             e `sincronizar` grava o .docx antes de a IA ler, senão ela responde
             sobre uma versão que já não é a da tela. */
            aoMarcar={({ trecho, sincronizar }) => {
              setTrechoMarcado(trecho);
              sincronizarPeca.current = sincronizar;
            }}
          />
        ) : ocupado === "minuta" ? (
          /* Sem peça aberta ainda não existe folha onde escrever: aqui a montagem
           aparece na prévia, com o mesmo botão de desistir. */
          <div className="relative min-w-0 flex-1">
            <PecaNascendo texto={rascunhoPeca} aoCancelar={cancelarMinuta} />
          </div>
        ) : (
          <Preparo
            nome={atual.nome}
            caso={caso}
            provas={provas ?? []}
            ficha={ficha}
            /* O esquema para o rótulo dos campos que faltam: sem ele a tela
               imprime a chave do banco ("cpf, resumo_atendimento"). */
            esquema={esquema}
            passoDaFicha={passoDaFicha && pode(passoDaFicha.permissao) ? passoDaFicha : null}
            aoPreencherFicha={executarPasso}
            aoExtrairFatos={extrairFatos}
            aoMontar={gerarMinuta}
            /* As três saídas que faltavam. Fechar a petição não perde nada aqui:
               a peça ainda não existe, e é por isso que esta tela está aberta. */
            aoAbrirFicha={abrirFicha}
            aoConferir={() => {
              fecharPeticao();
              ajustar({ conferir: true });
            }}
            aoGravarReuniao={() => {
              fecharPeticao();
              ajustar({ painel: "reunioes" });
            }}
            aoFechar={fecharPeticao}
            ocupado={ocupado}
          />
        )}

        {/*
         * SEM PEÇA, sem coluna.
         *
         * A conversa sobre a peça só existe depois que a peça existe — e a coluna
         * ficava lá, 380px, dizendo isso. Numa tela cuja única tarefa é "montar",
         * era um terço da largura ocupado por um aviso sobre o futuro, ao lado do
         * cartão que a pessoa veio ler. Montada a peça, ela entra. O ✕ que era
         * dela passou para o `Preparo`.
         */}
        {peca && !empilha ? (
          <div className="flex min-h-0 w-[380px] min-w-0 flex-none flex-col border-l border-l-border">
            {conversaCom(fecharPeticao)}
          </div>
        ) : null}
      </div>

      {/* Onde empilha, a conversa é FOLHA: `Cortina` já nasce colada embaixo no
          celular (`FOLHA_NO_CELULAR`), que é o gesto do aparelho — e é o mesmo
          componente das ações da mensagem, então quem já usou uma conhece a
          outra. `moldura` dá a altura de trabalho: conversa em caixa de 300px é
          uma linha de cada vez. */}
      {peca && empilha && conversaAberta ? (
        <Cortina
          aoFechar={() => setConversaAberta(false)}
          rotulo="Conversa sobre a peça"
          largura="520px"
          moldura="height:88dvh"
          z={44}
        >
          {conversaCom(() => setConversaAberta(false))}
        </Cortina>
      ) : null}
    </div>
  );
}

/**
 * O editor da peça, com as duas funções que falam com o servidor.
 *
 * Elas ficam presas ao id da peça (`useCallback`): o editor recarrega os bytes
 * quando `carregar` muda, e uma função nova a cada render reabriria o documento
 * em loop, jogando fora a edição em andamento.
 */
function Peca({ peca, nome, aoPronto, aoMudarSujo, aoMarcar }) {
  const ocupado = useOcupado();
  const { avisar, documentoDaPeca, salvarDocumentoDaPeca, gerarMinuta, cancelarMinuta } = useAcoes();

  const carregar = useCallback(() => documentoDaPeca(peca.id), [documentoDaPeca, peca.id]);
  const salvar = useCallback((bytes) => salvarDocumentoDaPeca(peca.id, bytes), [salvarDocumentoDaPeca, peca.id]);
  const { Editor: EditorPeca, erro, tentarDeNovo } = useEditorDaPeca();

  if (erro)
    return (
      <div className="flex flex-1 items-center justify-center p-4">
        <Falha aoTentar={tentarDeNovo}>O editor da petição não abriu. A peça está salva; tente de novo.</Falha>
      </div>
    );
  if (!EditorPeca)
    return <div className="flex flex-1 items-center justify-center text-corpo text-muted-foreground">Abrindo o editor…</div>;

  return (
    <EditorPeca
      pecaId={peca.id}
      titulo={`${peca.tipo === "inicial" ? "Petição inicial" : peca.tipo} — ${nome}`}
      subtitulo={`v${peca.versao}${peca.atualizado_em ? ` · ${dataHora(peca.atualizado_em)}` : ""}`}
      carregar={carregar}
      salvar={salvar}
      aoAvisar={avisar}
      aoGerar={gerarMinuta}
      aoCancelar={cancelarMinuta}
      gerando={ocupado === "minuta"}
      aoPronto={aoPronto}
      aoMudarSujo={aoMudarSujo}
      /* O trecho marcado na folha sobe até a conversa: é o "isto" de
           "reescreva isto". Sem repassar aqui, o fio ficava partido no meio e a
           caixa do trecho nunca aparecia. */
      aoMarcar={aoMarcar}
    />
  );
}
